import { Decoration } from "@codemirror/view";
import type { EditorState } from "@codemirror/state";
import type { LivePreviewCursor } from "./cursor";
import type { Tree } from "@lezer/common";

interface DecorationEntry {
  from: number;
  to: number;
  decoration: Decoration;
}

const linkMark = Decoration.mark({ class: "cm-md-link" });

/**
 * Build decorations for markdown links `[text](url)`.
 *
 * When the cursor is NOT on the link's line, the bracket/URL syntax is
 * hidden and only the link text is shown, styled with `.cm-md-link`.
 * Image nodes are explicitly skipped.
 *
 * `excludeRanges` allows other decoration builders (e.g. context references)
 * to claim ranges so the link builder skips Link nodes that overlap them.
 */
export function buildLinkDecorations(
  state: EditorState,
  tree: Tree,
  range: { from: number; to: number },
  cursor: LivePreviewCursor,
  decorations: DecorationEntry[],
  excludeRanges?: Array<{ from: number; to: number }>,
): void {
  const cursorLine = cursor.cursorLine;

  tree.iterate({
    from: range.from,
    to: range.to,
    enter(node) {
      // Skip Image nodes
      if (node.name === "Image") return false;

      if (node.name !== "Link") return;

      // Skip links that overlap with excluded ranges (e.g. context references).
      // The markdown parser sees [label](ref:...) as a Link, but context
      // references handle the full @[label](ref:...) syntax.
      if (excludeRanges) {
        for (const ex of excludeRanges) {
          if (node.from < ex.to && node.to > ex.from) {
            return false;
          }
        }
      }

      const nodeLine = state.doc.lineAt(node.from).number;

      // If cursor is on this line, show raw markdown
      if (cursorLine === nodeLine) return false;

      // Collect LinkMark and URL positions
      const linkMarks: Array<{ from: number; to: number }> = [];
      let urlFrom = -1;
      let urlTo = -1;

      const childCursor = node.node.cursor();
      if (childCursor.firstChild()) {
        do {
          if (childCursor.name === "LinkMark") {
            linkMarks.push({
              from: childCursor.from,
              to: childCursor.to,
            });
          }
          if (childCursor.name === "URL") {
            urlFrom = childCursor.from;
            urlTo = childCursor.to;
          }
        } while (childCursor.nextSibling());
      }

      // We expect at least 2 LinkMarks: [ and ]
      // And a URL section
      if (linkMarks.length < 2 || urlFrom === -1) return false;

      // Hide the opening `[`
      decorations.push({
        from: linkMarks[0].from,
        to: linkMarks[0].to,
        decoration: Decoration.replace({}),
      });

      // Hide `](url)` — from the closing `]` through to the end of the link
      // The closing ] is linkMarks[1], and we need to hide through the end
      // which includes the `(url)` part
      decorations.push({
        from: linkMarks[1].from,
        to: node.to,
        decoration: Decoration.replace({}),
      });

      // Style the link text (between opening [ and closing ])
      const textFrom = linkMarks[0].to;
      const textTo = linkMarks[1].from;
      if (textFrom < textTo) {
        decorations.push({
          from: textFrom,
          to: textTo,
          decoration: linkMark,
        });
      }

      return false;
    },
  });
}
